import axios from "axios";
import { Settings } from "../../../settings";

/* -------------------------------------------------------
   INLINE simulateWorker.js
------------------------------------------------------- */
const simulateWorkerCode = `
  importScripts("https://cdn.jsdelivr.net/npm/axios/dist/axios.min.js");

  onmessage = async (event) => {
    const { bet, token, apiBaseUrl, slotface, gameName } = event.data;

    try {
      const response = await axios.post(
        apiBaseUrl + "/x/simulate",
        {
          game: gameName,
          bet: bet,
          slotface: slotface
        },
        {
          headers: {
            "rs-token": token,
            "simkey": "simkey1015%"
          }
        }
      );

      postMessage({
        balance: response.data.balance,
        round: response.data.round,
        token: response.headers["rs-token"],
        bet: response.data.round[0].bet
      });

    } catch (error) {
      postMessage({ error: error.message });
    }
  };
`;

/* -------------------------------------------------------
   INLINE spinWorker.js
------------------------------------------------------- */
const spinWorkerCode = `
  importScripts("https://cdn.jsdelivr.net/npm/axios/dist/axios.min.js");

  onmessage = async (event) => {
    const { bet, token, apiBaseUrl, gameName } = event.data;

    try {
      const response = await axios.post(
        apiBaseUrl + "/x/redstone/demo/play",
        {
          game: gameName,
          bet: bet
        },
        {
          headers: {
            "rs-token": token
          }
        }
      );

      postMessage({
        token: response.headers["rs-token"],
        round: response.data.round,
        bet: response.data.bet,
        balance: response.data.balance
      });

    } catch (error) {
      postMessage({ error: error.message });
    }
  };
`;

/* -------------------------------------------------------
   WORKER FACTORY AS BLOB
------------------------------------------------------- */
function createInlineWorker(code) {
  const blob = new Blob([code], { type: "application/javascript" });
  const url = URL.createObjectURL(blob);
  return new Worker(url);
}

const simulateWorker = createInlineWorker(simulateWorkerCode);
const worker = createInlineWorker(spinWorkerCode);

/* -------------------------------------------------------
   MAIN RGS PROVIDER
------------------------------------------------------- */
class RedstoneRGS {
  constructor() {
    this.api_base_url = "https://auth-beta.redstone.rs";
  }

  async initGame() {
    const response = await axios.post(
      `${this.api_base_url}/x/redstone/demo/init`,
      {
        game: Settings.APP.GAME_NAME,
        currency: Settings.APP.CURRENCY,
      }
    );

    return {
      bet: response.data.session.bet,
      balance: response.data.session.balance,
      token: response.headers["rs-token"],
      currency: response.data.session.currency,
      game: response.data.session.game,
      bets: response.data.session.bets,
      winningLines: response.data.game.traits.lines,
      paytable: response.data.game.traits.paytable,
    };
  }

  spin(bet, token, slotface = null) {
    return new Promise((resolve, reject) => {
      const workerToUse = Settings.SIMULATION_MODE ? simulateWorker : worker;

      workerToUse.onmessage = (event) => {
        const data = event.data;
        if (data.error) reject(data.error);
        else resolve(data);
      };

      workerToUse.postMessage({
        bet,
        token,
        apiBaseUrl: this.api_base_url,
        gameName: Settings.APP.GAME_NAME,
        ...(Settings.SIMULATION_MODE && { slotface }),
      });
    });
  }

  async simulate(slotface, bet, token) {
    return this.spin(bet, token, slotface);
  }

  transformReelsSymbols(response) {
    return response;
  }

  getResponseHandler() {
    return {
      patterns: {
        freeSpins: [
          {
            condition: (response) =>
              response.round?.length > 1 &&
              response.round[0]?.type === "feature",
            extractFreeSpins: (round) => round.slice(1),
            extractTriggerRound: (round) => round[0],
          },
          {
            condition: (response) =>
              response.round?.type === "feature" &&
              response.round?.freeSpins > 0,
            extractFreeSpins: (round) => [round],
            extractTriggerRound: (round) => round,
          },
        ],
        regular: {
          condition: (response) =>
            response.round &&
            (response.round.length === 1 ||
              response.round[0]?.type !== "feature"),
          extractRound: (round) => (round.length > 0 ? round[0] : round),
        },
      },
      propertyMap: {
        balance: "balance",
        bet: "bet",
        win: "win",
        token: "token",
        round: "round",
        type: "type",
        reels: "reels",
        wins: "wins",
        freeSpins: "freeSpins",
      },
    };
  }
}

export default RedstoneRGS;
